import React, { useEffect, useState, useMemo } from "react";
import { supabase } from "./supabaseClient.js";
import TmSelect from "./TmSelect.jsx";

const PRESETS = {
  notebook: { paper: "#ffffff", gridColor: "#d4e4f0", gridStrong: "#7ab0d4", accent: "#d63a7a" },
  mint:     { paper: "#f0fdf9", gridColor: "#bbf7d0", gridStrong: "#4ade80", accent: "#059669" },
  lavender: { paper: "#faf5ff", gridColor: "#e9d5ff", gridStrong: "#a78bfa", accent: "#7c3aed" },
  rose:     { paper: "#fff1f2", gridColor: "#fecdd3", gridStrong: "#fb7185", accent: "#e11d48" },
  sky:      { paper: "#f0f9ff", gridColor: "#bae6fd", gridStrong: "#38bdf8", accent: "#0284c7" },
  amber:    { paper: "#fffbeb", gridColor: "#fde68a", gridStrong: "#f59e0b", accent: "#d97706" },
  slate:    { paper: "#f8fafc", gridColor: "#cbd5e1", gridStrong: "#64748b", accent: "#334155" },
  dark:     { paper: "#0f172a", gridColor: "#1e293b", gridStrong: "#334155", accent: "#f472b6" }
};

const SHAPES = [
  ["grid", "شبكة"], ["lined", "خطوط أفقية"], ["dots", "نقاط"], ["dots-lines", "نقاط + خطوط"],
  ["diamond", "معينات"], ["double", "مزدوج"], ["cross", "متقاطع"], ["none", "بدون"]
];

const LOGO_FX = [
  ["none", "بدون"], ["glow", "توهج"], ["pulse", "نبض"], ["float", "طفو"],
  ["rotate", "دوران"], ["bounce", "قفز"], ["shake", "اهتزاز"], ["flip", "قلب"]
];

const FRAMES = [["none", "بدون"], ["circle", "دائري"], ["rounded", "مربع"], ["glow-ring", "هالة"]];

const FONTS = [
  ["Cairo", "القاهرة"], ["Tajawal", "تجوال"], ["Amiri", "أميري"],
  ["Noto Sans Arabic", "نوتو"], ["Reem Kufi", "ريم كوفي"], ["Aref Ruqaa", "رقعة"],
  ["Scheherazade New", "شهرزاد"], ["Lateef", "لطيف"], ["JetBrains Mono", "جيت برينز"]
];

const DEFAULT_S = {
  paper: "#ffffff", gridColor: "#d4e4f0", gridStrong: "#7ab0d4",
  gridSize: 24, gridThickness: 1, gridOpacity: 100,
  gridShape: "grid", animationSpeed: 0.8,
  gridOn: true, hLinesOn: true, vLinesOn: true,
  cardOpacity: 5, accent: "#d63a7a", accentHover: "#b02962",
  textDark: "#f6faff", textLight: "#1a2a3a",
  logoSize: 68, logoFx: "none", logoShadow: 35,
  logoBrightness: 100, logoSaturation: 100, logoHue: 0, logoFrame: "none",
  fontFamily: "Cairo", fontSize: 14, fontWeight: 600, letterSpacing: 0
};

export default function UIControl({ onClose, embedded = false }) {
  const [s, setS] = useState(DEFAULT_S);
  const [initial, setInitial] = useState(DEFAULT_S);
  const [rowId, setRowId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [tab, setTab] = useState("bg");

  useEffect(() => { (async () => {
    try {
      const { data } = await supabase.from("platform_ui_settings").select("id, settings").eq("is_active", true).limit(1).maybeSingle();
      if (data) {
        setRowId(data.id);
        const loaded = { ...DEFAULT_S, ...(data.settings || {}) };
        setS(loaded); setInitial(loaded);
      }
    } catch {}
    setLoading(false);
  })(); }, []);

  const dirty = useMemo(() => JSON.stringify(s) !== JSON.stringify(initial), [s, initial]);

  const applyPreset = (key) => {
    const p = PRESETS[key];
    if (p) setS({ ...s, ...p });
  };

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      const payload = { settings: s, updated_at: new Date().toISOString() };
      let result;
      if (rowId) result = await supabase.from("platform_ui_settings").update(payload).eq("id", rowId).select();
      else { result = await supabase.from("platform_ui_settings").insert(payload).select("id").maybeSingle(); if (result.data) setRowId(result.data.id); }
      if (result.error) throw result.error;
      setInitial(s);
      setMsg("حُفظت ونُشرت للجميع");
      setTimeout(() => setMsg(""), 3000);
    } catch (e) { setMsg("تعذّر: " + (e.message || e)); }
    setSaving(false);
  };

  const reset = () => setS(DEFAULT_S);
  const cancel = () => { setS(initial); setMsg(""); };

  return (
    <div className={embedded ? "nb-embedded-wrapper" : "ag-modal-bg u4-bg"} onClick={embedded ? undefined : onClose} style={embedded ? { position: "relative", background: "transparent", padding: 0 } : {}}>
      <style>{`
        .nb-embedded-wrapper .nb-modal { box-shadow: none !important; border: 1px solid var(--line) !important; width: 100% !important; max-width: 100% !important; max-height: 65vh !important; animation: none !important; } .nb-modal { background: #ffffff; background-image: linear-gradient(to right, #d4e4f0 1px, transparent 1px), linear-gradient(to bottom, #d4e4f0 1px, transparent 1px); background-size: 24px 24px; border-radius: 14px; padding: 0; max-width: 820px; width: 94vw; max-height: 90vh; box-shadow: 0 30px 80px rgba(122,176,212,0.35); border: 1px solid rgba(122,176,212,0.2); animation: nbUp .3s ease-out both; position: relative; overflow: hidden; display: flex; flex-direction: column; }
        @keyframes nbUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: none; } }
        .nb-header { display: flex; align-items: center; gap: 12px; padding: 18px 26px 14px; border-bottom: 1.5px solid #7ab0d4; flex-shrink: 0; background: rgba(255,255,255,0.95); }
        .nb-title-mark { display: inline-flex; align-items: center; justify-content: center; width: 42px; height: 42px; background: linear-gradient(135deg, #d63a7a, #b02962); border-radius: 11px; color: white; box-shadow: 0 6px 16px rgba(214,58,122,0.3); }
        .nb-title { font-family: 'Cairo', sans-serif; font-size: 20px; font-weight: 800; color: #1a2a3a; }
        .nb-title span { color: #d63a7a; }
        .nb-subtitle { font-size: 11.5px; color: #8a9aa8; font-weight: 500; }
        .nb-msg { font-size: 12px; font-weight: 700; color: #2e8a5f; background: rgba(46,138,95,0.1); padding: 4px 10px; border-radius: 20px; }
        .nb-close { display: inline-flex; align-items: center; justify-content: center; width: 34px; height: 34px; background: white; border: 1.5px solid #e0e8ef; border-radius: 8px; color: #4a5a6a; cursor: pointer; transition: all .2s; }
        .nb-close:hover { border-color: #7ab0d4; color: #7ab0d4; }
        .nb-tabs { display: flex; gap: 4px; margin: 14px 26px 0; background: rgba(255,255,255,0.9); padding: 5px; border-radius: 10px; border: 1px solid #e0e8ef; flex-shrink: 0; }
        .nb-tab { flex: 1; font-family: 'Cairo', sans-serif; font-size: 12.5px; font-weight: 700; padding: 8px 6px; background: transparent; border: none; border-radius: 7px; color: #4a5a6a; cursor: pointer; transition: all .2s; }
        .nb-tab:hover { color: #d63a7a; }
        .nb-tab.on { background: white; color: #d63a7a; box-shadow: 0 2px 6px rgba(122,176,212,0.15); }
        .nb-content { flex: 1; overflow-y: auto; padding: 12px 26px 16px; }
        .nb-section { font-family: 'Cairo', sans-serif; font-size: 12.5px; font-weight: 800; color: #d63a7a; margin: 14px 0 10px; letter-spacing: 0.4px; text-transform: uppercase; display: flex; align-items: center; gap: 8px; }
        .nb-section:first-child { margin-top: 0; }
        .nb-section::before { content: ""; width: 20px; height: 2px; background: #d63a7a; }
      `}</style>
      <style>{`
        .nb-row { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; padding: 7px 10px; background: rgba(255,255,255,0.7); border-radius: 8px; border: 1px solid #e0e8ef; transition: all .18s; }
        .nb-row:hover { border-color: #7ab0d4; background: rgba(255,255,255,0.9); }
        .nb-lbl { font-family: 'Cairo', sans-serif; font-size: 12.5px; font-weight: 600; color: #1a2a3a; min-width: 110px; }
        .nb-val { font-family: 'JetBrains Mono', monospace; font-size: 11px; font-weight: 700; color: #d63a7a; background: rgba(214,58,122,0.08); padding: 2px 8px; border-radius: 5px; min-width: 40px; text-align: center; }
        .nb-slider { flex: 1; height: 4px; background: linear-gradient(to right, #d4e4f0, #7ab0d4); border-radius: 3px; -webkit-appearance: none; appearance: none; cursor: pointer; outline: none; }
        .nb-slider::-webkit-slider-thumb { -webkit-appearance: none; width: 16px; height: 16px; background: #d63a7a; border-radius: 50%; cursor: pointer; box-shadow: 0 2px 6px rgba(214,58,122,0.4); }
        .nb-slider::-moz-range-thumb { width: 16px; height: 16px; background: #d63a7a; border-radius: 50%; border: none; cursor: pointer; }
        .nb-color { width: 40px; height: 28px; padding: 0; border: 1.5px solid #e0e8ef; border-radius: 6px; background: transparent; cursor: pointer; }
        .nb-color:hover { border-color: #7ab0d4; }
        .nb-hex { font-family: 'JetBrains Mono', monospace; font-size: 11px; padding: 5px 8px; width: 80px; border: 1.5px solid #e0e8ef; border-radius: 6px; background: white; color: #1a2a3a; }
        .nb-hex:focus { outline: none; border-color: #7ab0d4; }
        .nb-select { font-family: 'Cairo', sans-serif; font-size: 12.5px; padding: 6px 10px; border: 1.5px solid #e0e8ef; border-radius: 6px; background: white; color: #1a2a3a; cursor: pointer; flex: 1; }
        .nb-select:focus { outline: none; border-color: #7ab0d4; }
        .nb-switch { position: relative; display: inline-block; width: 40px; height: 22px; }
        .nb-switch input { opacity: 0; width: 0; height: 0; }
        .nb-slider-track { position: absolute; cursor: pointer; inset: 0; background: #e0e8ef; border-radius: 22px; transition: .3s; }
        .nb-slider-track::before { content: ""; position: absolute; height: 16px; width: 16px; right: 3px; top: 3px; background: white; border-radius: 50%; transition: .3s; box-shadow: 0 1px 3px rgba(0,0,0,0.15); }
        .nb-switch input:checked + .nb-slider-track { background: linear-gradient(135deg, #d63a7a, #b02962); }
        .nb-switch input:checked + .nb-slider-track::before { transform: translateX(-18px); }
      `}</style>
      <style>{`
        .nb-preset-grid { display: grid; grid-template-columns: repeat(8, 1fr); gap: 6px; margin-bottom: 10px; }
        .nb-preset-btn { aspect-ratio: 1; border: 2px solid; border-radius: 8px; cursor: pointer; transition: transform .2s; padding: 0; }
        .nb-preset-btn:hover { transform: scale(1.1); }
        .nb-shape-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; margin-bottom: 10px; }
        .nb-shape-btn { font-family: 'Cairo', sans-serif; font-size: 11px; font-weight: 700; padding: 8px 4px; background: white; border: 1.5px solid #e0e8ef; border-radius: 7px; color: #4a5a6a; cursor: pointer; transition: all .18s; }
        .nb-shape-btn:hover { border-color: #7ab0d4; color: #7ab0d4; }
        .nb-shape-btn.on { background: #d63a7a; color: white; border-color: #d63a7a; }
        .nb-fx-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; margin-bottom: 10px; }
        .nb-frame-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; margin-bottom: 10px; }
        .nb-layer-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; margin-bottom: 10px; }
        .nb-align-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; margin-bottom: 10px; }
        .nb-actions { display: flex; align-items: center; gap: 10px; padding: 14px 26px 16px; border-top: 1.5px solid #7ab0d4; flex-shrink: 0; background: rgba(255,255,255,0.95); }
        .nb-btn { font-family: 'Cairo', sans-serif; font-size: 12.5px; font-weight: 700; padding: 8px 16px; border-radius: 8px; border: 1.5px solid #e0e8ef; background: white; color: #4a5a6a; cursor: pointer; transition: all .18s; }
        .nb-btn:hover { border-color: #7ab0d4; color: #7ab0d4; }
        .nb-btn-primary { font-family: 'Cairo', sans-serif; font-size: 13.5px; font-weight: 800; padding: 10px 24px; border-radius: 9px; border: none; background: linear-gradient(135deg, #d63a7a, #b02962); color: white; cursor: pointer; box-shadow: 0 4px 12px rgba(214,58,122,0.3); transition: all .2s; }
        .nb-btn-primary:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 22px rgba(214,58,122,0.45); }
        .nb-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
        .nb-hint { font-size: 11px; color: #8a9aa8; margin-inline-end: auto; }
        .nb-hint.on { color: #d63a7a; font-weight: 700; }
      `}</style>

      <div className="nb-modal" onClick={(e) => e.stopPropagation()}>
        <div className="nb-header">
          <span className="nb-title-mark">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 8l6 6"/><path d="M4 14l6-6 2-3"/><path d="M2 5h12"/><path d="M22 22l-5-10-5 10"/><path d="M14 18h6"/></svg>
          </span>
          <div style={{ flex: 1 }}>
            <div className="nb-title">التحكم بواجهة <span>ترجمتك</span></div>
            <div className="nb-subtitle">صفحة تسجيل الدخول · معاينة مباشرة · نشر للجميع</div>
          </div>
          {msg && <span className="nb-msg">{msg}</span>}
          {!embedded && <button className="nb-close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>}
        </div>

        {loading ? <div style={{ padding: 40, textAlign: "center", color: "#8a9aa8" }}>جارٍ التحميل…</div> : (<>

        <div className="nb-tabs">
          <button className={"nb-tab " + (tab === "bg" ? "on" : "")} onClick={() => setTab("bg")}>الخلفية</button>
          <button className={"nb-tab " + (tab === "colors" ? "on" : "")} onClick={() => setTab("colors")}>الألوان</button>
          <button className={"nb-tab " + (tab === "logo" ? "on" : "")} onClick={() => setTab("logo")}>الشعار</button>
          <button className={"nb-tab " + (tab === "type" ? "on" : "")} onClick={() => setTab("type")}>الخطوط</button>
          <button className={"nb-tab " + (tab === "card" ? "on" : "")} onClick={() => setTab("card")}>البطاقة</button>
        </div>

        <div className="nb-content">

        {tab === "bg" && (
          <div>
            <div className="nb-section">قوالب جاهزة</div>
            <div className="nb-preset-grid">
              {Object.entries(PRESETS).map(([key, p]) => (
                <button key={key} className="nb-preset-btn" onClick={() => applyPreset(key)}
                  style={{ background: p.paper, borderColor: p.gridStrong }} title={key} />
              ))}
            </div>

            <div className="nb-section">شكل الشبكة</div>
            <div className="nb-shape-grid">
              {SHAPES.map(([key, name]) => (
                <button key={key} className={"nb-shape-btn " + (s.gridShape === key ? "on" : "")}
                  onClick={() => setS({ ...s, gridShape: key })}>{name}</button>
              ))}
            </div>

            <div className="nb-section">التخصيص</div>
            <div className="nb-row">
              <span className="nb-lbl">حجم المربع</span>
              <input type="range" className="nb-slider" min="8" max="80" value={s.gridSize}
                onChange={(e) => setS({ ...s, gridSize: +e.target.value })} />
              <span className="nb-val">{s.gridSize}px</span>
            </div>
            <div className="nb-row">
              <span className="nb-lbl">سُمك الخطوط</span>
              <input type="range" className="nb-slider" min="1" max="4" step="0.5" value={s.gridThickness}
                onChange={(e) => setS({ ...s, gridThickness: +e.target.value })} />
              <span className="nb-val">{s.gridThickness}px</span>
            </div>
            <div className="nb-row">
              <span className="nb-lbl">شفافية الشبكة</span>
              <input type="range" className="nb-slider" min="10" max="100" value={s.gridOpacity}
                onChange={(e) => setS({ ...s, gridOpacity: +e.target.value })} />
              <span className="nb-val">{s.gridOpacity}%</span>
            </div>
            <div className="nb-row">
              <span className="nb-lbl">سرعة الحركة</span>
              <input type="range" className="nb-slider" min="0.3" max="2" step="0.1" value={s.animationSpeed}
                onChange={(e) => setS({ ...s, animationSpeed: +e.target.value })} />
              <span className="nb-val">{s.animationSpeed}s</span>
            </div>

            <div className="nb-section">الطبقات</div>
            <div className="nb-layer-grid">
              <button className={"nb-shape-btn " + (s.gridOn ? "on" : "")}
                onClick={() => setS({ ...s, gridOn: !s.gridOn })}>شبكة</button>
              <button className={"nb-shape-btn " + (s.hLinesOn ? "on" : "")}
                onClick={() => setS({ ...s, hLinesOn: !s.hLinesOn })}>أفقي</button>
              <button className={"nb-shape-btn " + (s.vLinesOn ? "on" : "")}
                onClick={() => setS({ ...s, vLinesOn: !s.vLinesOn })}>عمودي</button>
            </div>
          </div>
        )}

        {tab === "colors" && (
          <div>
            <div className="nb-section">ألوان الخلفية</div>
            <div className="nb-row">
              <span className="nb-lbl">لون الورقة</span>
              <input type="color" className="nb-color" value={s.paper}
                onChange={(e) => setS({ ...s, paper: e.target.value })} />
              <input type="text" className="nb-hex" value={s.paper}
                onChange={(e) => setS({ ...s, paper: e.target.value })} />
            </div>
            <div className="nb-row">
              <span className="nb-lbl">لون الشبكة</span>
              <input type="color" className="nb-color" value={s.gridColor}
                onChange={(e) => setS({ ...s, gridColor: e.target.value })} />
              <input type="text" className="nb-hex" value={s.gridColor}
                onChange={(e) => setS({ ...s, gridColor: e.target.value })} />
            </div>
            <div className="nb-row">
              <span className="nb-lbl">لون الخطوط القوية</span>
              <input type="color" className="nb-color" value={s.gridStrong}
                onChange={(e) => setS({ ...s, gridStrong: e.target.value })} />
              <input type="text" className="nb-hex" value={s.gridStrong}
                onChange={(e) => setS({ ...s, gridStrong: e.target.value })} />
            </div>

            <div className="nb-section">اللون المميّز</div>
            <div className="nb-row">
              <span className="nb-lbl">اللون الأساسي</span>
              <input type="color" className="nb-color" value={s.accent}
                onChange={(e) => setS({ ...s, accent: e.target.value })} />
              <input type="text" className="nb-hex" value={s.accent}
                onChange={(e) => setS({ ...s, accent: e.target.value })} />
            </div>
            <div className="nb-row">
              <span className="nb-lbl">لون التحويم</span>
              <input type="color" className="nb-color" value={s.accentHover}
                onChange={(e) => setS({ ...s, accentHover: e.target.value })} />
              <input type="text" className="nb-hex" value={s.accentHover}
                onChange={(e) => setS({ ...s, accentHover: e.target.value })} />
            </div>

            <div className="nb-section">النصوص</div>
            <div className="nb-row">
              <span className="nb-lbl">النص في النهاري</span>
              <input type="color" className="nb-color" value={s.textLight}
                onChange={(e) => setS({ ...s, textLight: e.target.value })} />
              <input type="text" className="nb-hex" value={s.textLight}
                onChange={(e) => setS({ ...s, textLight: e.target.value })} />
            </div>
            <div className="nb-row">
              <span className="nb-lbl">النص في الليلي</span>
              <input type="color" className="nb-color" value={s.textDark}
                onChange={(e) => setS({ ...s, textDark: e.target.value })} />
              <input type="text" className="nb-hex" value={s.textDark}
                onChange={(e) => setS({ ...s, textDark: e.target.value })} />
            </div>
          </div>
        )}

        {tab === "logo" && (
          <div>
            <div className="nb-section">حجم الشعار</div>
            <div className="nb-row">
              <span className="nb-lbl">الحجم</span>
              <input type="range" className="nb-slider" min="40" max="140" value={s.logoSize}
                onChange={(e) => setS({ ...s, logoSize: +e.target.value })} />
              <span className="nb-val">{s.logoSize}px</span>
            </div>

            <div className="nb-section">تأثير الحركة</div>
            <div className="nb-fx-grid">
              {LOGO_FX.map(([key, name]) => (
                <button key={key} className={"nb-shape-btn " + (s.logoFx === key ? "on" : "")}
                  onClick={() => setS({ ...s, logoFx: key })}>{name}</button>
              ))}
            </div>

            <div className="nb-section">تعديلات لونية</div>
            <div className="nb-row">
              <span className="nb-lbl">شدة الظل</span>
              <input type="range" className="nb-slider" min="0" max="100" value={s.logoShadow}
                onChange={(e) => setS({ ...s, logoShadow: +e.target.value })} />
              <span className="nb-val">{s.logoShadow}%</span>
            </div>
            <div className="nb-row">
              <span className="nb-lbl">السطوع</span>
              <input type="range" className="nb-slider" min="50" max="150" value={s.logoBrightness}
                onChange={(e) => setS({ ...s, logoBrightness: +e.target.value })} />
              <span className="nb-val">{s.logoBrightness}%</span>
            </div>
            <div className="nb-row">
              <span className="nb-lbl">تشبع الألوان</span>
              <input type="range" className="nb-slider" min="0" max="200" value={s.logoSaturation}
                onChange={(e) => setS({ ...s, logoSaturation: +e.target.value })} />
              <span className="nb-val">{s.logoSaturation}%</span>
            </div>
            <div className="nb-row">
              <span className="nb-lbl">تعديل اللون</span>
              <input type="range" className="nb-slider" min="0" max="360" value={s.logoHue}
                onChange={(e) => setS({ ...s, logoHue: +e.target.value })} />
              <span className="nb-val">{s.logoHue}°</span>
            </div>

            <div className="nb-section">إطار الشعار</div>
            <div className="nb-frame-grid">
              {FRAMES.map(([key, name]) => (
                <button key={key} className={"nb-shape-btn " + (s.logoFrame === key ? "on" : "")}
                  onClick={() => setS({ ...s, logoFrame: key })}>{name}</button>
              ))}
            </div>
          </div>
        )}

        {tab === "type" && (
          <div>
            <div className="nb-section">الخط</div>
            <div className="nb-row">
              <span className="nb-lbl">اختيار الخط</span>
              <TmSelect size="sm" value={s.fontFamily} onChange={(v) => setS({ ...s, fontFamily: v })} options={FONTS} />
            </div>
            <div className="nb-row">
              <span className="nb-lbl">حجم الخط</span>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}><span style={{ fontSize: 12, minWidth: 90 }}>مقياس الموقع</span><input type="range" min="85" max="130" step="5" value={s.siteScale || 100} onChange={(e) => setS({ ...s, siteScale: +e.target.value })} style={{ flex: 1 }} /><span style={{ fontSize: 11 }}>{s.siteScale || 100}%</span></div>
              <input type="range" className="nb-slider" min="8" max="48" value={s.fontSize}
                onChange={(e) => setS({ ...s, fontSize: +e.target.value })} />
              <span className="nb-val">{s.fontSize}px</span>
            </div>
            <div className="nb-row">
              <span className="nb-lbl">سُمك الخط</span>
              <TmSelect size="sm" value={String(s.fontWeight)} onChange={(v) => setS({ ...s, fontWeight: +v })} options={[["300","رفيع (Light)"],["400","عادي (Regular)"],["500","متوسط (Medium)"],["600","نصف عريض (SemiBold)"],["700","عريض (Bold)"],["800","عريض جدًا (ExtraBold)"],["900","أسود (Black)"]]} />
            </div>
            <div className="nb-row">
              <span className="nb-lbl">تباعد الحروف</span>
              <input type="range" className="nb-slider" min="-2" max="10" step="0.5" value={s.letterSpacing}
                onChange={(e) => setS({ ...s, letterSpacing: +e.target.value })} />
              <span className="nb-val">{s.letterSpacing}px</span>
            </div>

            <div className="nb-section">معاينة الخط</div>
            <div style={{ padding: 16, background: "white", border: "1px solid #e0e8ef", borderRadius: 8, fontFamily: `'${s.fontFamily}', sans-serif`, fontSize: s.fontSize, fontWeight: s.fontWeight, letterSpacing: s.letterSpacing + "px", color: s.textLight, textAlign: "center" }}>
              مرحبًا بعودتك — ترجم<span style={{ color: s.accent, fontWeight: 800 }}>تك</span>
            </div>
          </div>
        )}

        {tab === "card" && (
          <div>
            <div className="nb-section">شفافية البطاقة</div>
            <div className="nb-row">
              <span className="nb-lbl">درجة الشفافية</span>
              <input type="range" className="nb-slider" min="0" max="100" value={s.cardOpacity}
                onChange={(e) => setS({ ...s, cardOpacity: +e.target.value })} />
              <span className="nb-val">{s.cardOpacity}%</span>
            </div>
            <div className="nb-section">معاينة</div>
            <div style={{ padding: 20, background: s.paper, backgroundImage: `linear-gradient(to right, ${s.gridColor} 1px, transparent 1px), linear-gradient(to bottom, ${s.gridColor} 1px, transparent 1px)`, backgroundSize: `${s.gridSize}px ${s.gridSize}px`, borderRadius: 8, position: "relative" }}>
              <div style={{ maxWidth: 260, margin: "0 auto", padding: 20, background: `rgba(255,255,255,${s.cardOpacity / 100 + 0.85})`, backdropFilter: "blur(12px)", borderRadius: 12, boxShadow: "0 15px 40px rgba(122,176,212,0.25)", textAlign: "center" }}>
                <div style={{ display: "inline-flex", width: 48, height: 48, background: `linear-gradient(135deg, ${s.accent}, ${s.accentHover})`, borderRadius: 12, alignItems: "center", justifyContent: "center", marginBottom: 10 }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 8l6 6"/><path d="M22 22l-5-10-5 10"/></svg>
                </div>
                <div style={{ fontFamily: s.fontFamily, fontSize: 18, fontWeight: 800, color: s.textLight }}>ترجم<span style={{ color: s.accent }}>تك</span></div>
              </div>
            </div>
          </div>
        )}

        </div>
        </>)}

        <div className="nb-actions">
          <button className="nb-btn" onClick={reset}>استعادة الافتراضي</button>
          {dirty && <button className="nb-btn" onClick={cancel}>إلغاء التغييرات</button>}
          <span className={"nb-hint " + (dirty ? "on" : "")}>
            {dirty ? "توجد تغييرات غير محفوظة" : "لا تغييرات - كل المستخدمين يرون نفس التصميم"}
          </span>
          <button className="nb-btn-primary" onClick={save} disabled={!dirty || saving}>
            {saving ? "جارٍ النشر…" : "حفظ ونشر للجميع"}
          </button>
        </div>

      </div>
    </div>
  );
}
